import { useEffect, useState, type FC, useMemo } from "react";

import {
  Button,
  Tooltip,
  Alert,
  Combobox,
  FormField,
  Dialog,
  EmptyState,
} from "@hightouch/htui";

import { Link } from "src/router";

import { Editor } from "src/components/editor";
import { IntegrationIcon } from "src/components/integrations/integration-icon";
import {
  useGetSigmaWorkbooksQuery,
  useGetSigmaPagesQuery,
  useGetSigmaElementsQuery,
  useGetSigmaSqlQuery,
} from "src/graphql";
import { QueryType, type Sigma } from "src/types/models";

import { formatSourceSql } from "src/components/sql-editor/format-source-sql";
import {
  QueryContainer,
  QueryContent,
  QueryHeader,
  QueryHeaderTitle,
} from "src/components/query";

type Props = {
  queryError?: string;
  sigma?: Sigma;
  onChange: (sigmaModel: Sigma) => void;
  source: {
    id: string;
    name: string;
    type: string;
    definition: {
      name: string;
      icon: string;
      isSampleDataSource: boolean;
    };
  };
};

export const SigmaSelector: FC<Readonly<Props>> = ({
  sigma,
  onChange,
  source,
  queryError,
}) => {
  const sigmaQuery = sigma?.query;
  const [workbookId, setWorkbookId] = useState<string | undefined>(
    sigmaQuery?.workbookId,
  );
  const [pageId, setPageId] = useState<string | undefined>(sigmaQuery?.pageId);
  const [elementId, setElementId] = useState<string | undefined>(
    sigmaQuery?.elementId,
  );
  const [showSql, setShowSql] = useState(false);
  const [autoFillSelector, setAutoFillSelector] = useState<boolean>(true);

  const {
    data: workbooks,
    error: workbooksError,
    isLoading: loadingWorkbooks,
  } = useGetSigmaWorkbooksQuery(undefined, {
    select: (data) => data.getSigmaWorkbooks.workbooks,
  });

  const {
    data: pages,
    error: pagesError,
    isLoading: loadingPages,
  } = useGetSigmaPagesQuery(
    { workbookId: String(workbookId) },
    {
      enabled: Boolean(workbookId),
      select: (data) => data.getSigmaPages.pages,
    },
  );

  const {
    data: elements,
    error: elementsError,
    isLoading: loadingElements,
  } = useGetSigmaElementsQuery(
    { workbookId: String(workbookId), pageId: String(pageId) },
    {
      enabled: Boolean(workbookId) && Boolean(pageId),
      select: (data) => data.getSigmaElements.elements,
    },
  );

  const {
    data: elementSql,
    error: elementSqlError,
    isLoading: loadingElementSql,
  } = useGetSigmaSqlQuery(
    { workbookId: String(workbookId), elementId: String(elementId) },
    {
      enabled: Boolean(workbookId) && Boolean(elementId),
      select: (data) => data.getSigmaQuery?.sql,
    },
  );

  const formattedSql = useMemo(
    () => (elementSql ? formatSourceSql(source, elementSql) : ""),
    [elementSql, source],
  );

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    if (workbookId && pageId && elementId) {
      const sigmaModel: Sigma = {
        query: {
          queryType: QueryType.Sigma,
          elementId,
          pageId,
          workbookId,
        },
      };
      onChange(sigmaModel);
    }
  }, [elementId, pageId, workbookId]);

  useEffect(() => {
    if (autoFillSelector && sigma?.query?.workbookId) {
      setAutoFillSelector(false);
      setWorkbookId(sigma?.query?.workbookId);
      setPageId(sigma?.query?.pageId);
      setElementId(sigma?.query?.elementId);
    }
  }, [sigma, autoFillSelector]);

  if (workbooksError) {
    return (
      <EmptyState
        title="We were not able to connect to your Sigma instance."
        messageMaxW="100%"
        message={
          <>
            Please check that your Sigma credentials are configured correctly in{" "}
            <Link href="/extensions/sigma">settings</Link>.
          </>
        }
      />
    );
  }

  if (!loadingWorkbooks && workbooks?.length === 0) {
    return (
      <EmptyState
        title="Your Sigma account doesn't have any workbooks."
        message={
          <>
            Did you remember to publish your workbook? Please check that your
            Sigma credentials are configured correctly in{" "}
            <Link href="/extensions/sigma">settings</Link>.
          </>
        }
      />
    );
  }

  if (pagesError || elementsError || elementSqlError) {
    return (
      <EmptyState
        title="An error occured."
        messageMaxW="100%"
        message={
          <>
            Please check that your Sigma credentials are configured correctly in{" "}
            <Link href="/extensions/sigma">settings</Link>.
          </>
        }
      />
    );
  }

  let wrongDbWarning;
  const wrongDBErrors = [
    "cross-database references are not implemented",
    // Database FOO does not exist
    // Schema 'foo'."public" does not exist
    "does not exist or not authorized",
  ];
  if (
    queryError &&
    wrongDBErrors.some((errorString) => queryError.includes(errorString))
  ) {
    wrongDbWarning =
      "Are you sure the workbook's source connection in Sigma is the same as this model's source in Hightouch?";
  }

  return (
    <>
      {wrongDbWarning && (
        <Alert
          variant="inline"
          message={wrongDbWarning}
          title="Wrong database?"
          type="warning"
        />
      )}
      <QueryContainer>
        <QueryHeader>
          <QueryHeaderTitle
            icon={
              <IntegrationIcon
                src={source?.definition.icon}
                name={source?.definition.name}
              />
            }
            title={source?.name ?? "Private source"}
          />

          <Tooltip isDisabled={!!elementId} message="Please select an element">
            <Button
              isDisabled={!pageId || !elementId}
              isLoading={loadingElementSql}
              onClick={() => {
                setShowSql(true);
              }}
            >
              View SQL
            </Button>
          </Tooltip>
        </QueryHeader>
        <QueryContent>
          <FormField label="Workbook">
            <Combobox
              isLoading={loadingWorkbooks}
              options={
                workbooks?.map((workbook) => ({
                  value: workbook?.workbookId,
                  label: workbook?.name,
                })) ?? []
              }
              placeholder="Select a workbook…"
              value={workbookId}
              onChange={(workbookId) => {
                // Changing the workbook should clear the page and element drop downs.
                setElementId(undefined);
                setPageId(undefined);
                setWorkbookId(workbookId);
              }}
            />
          </FormField>

          <FormField label="Page">
            <Combobox
              isDisabled={!workbookId}
              isLoading={loadingPages}
              options={
                pages?.map((page) => ({
                  value: page?.pageId,
                  label: page?.name,
                })) ?? []
              }
              placeholder="Select a page…"
              value={pageId}
              onChange={(pageId) => {
                // Changing the page should clear the elements drop down.
                setElementId(undefined);
                setPageId(pageId);
              }}
            />
          </FormField>

          <FormField label="Element">
            <Combobox
              isDisabled={!pageId}
              isLoading={loadingElements}
              options={
                elements?.map((element) => ({
                  value: element?.elementId,
                  label: element?.name,
                })) ?? []
              }
              placeholder="Select an element…"
              value={elementId}
              onChange={setElementId}
            />
          </FormField>
        </QueryContent>

        <Dialog
          isOpen={showSql}
          variant="info"
          width="xl"
          title="SQL"
          actions={<Button onClick={() => setShowSql(false)}>Close</Button>}
          onClose={() => setShowSql(false)}
        >
          <Editor readOnly language="sql" value={formattedSql} />
        </Dialog>
      </QueryContainer>
    </>
  );
};
